import React from 'react';
import { ArrowUpRight } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { Section } from '../fx/AccentContext';
import { MaskedLines } from '../fx/MaskedLines';
import LivingImage from '../fx/LivingImage';
import { MagneticCTA, SectionLabel, PADX } from './ui';
import { EASE_EXPO } from '../fx/ease';
import {
  BOOTCAMP_NOTICE,
  BOOTCAMP_PROGRAMS,
  BOOTCAMP_URL,
} from '../../data/publicLinks';

const BootcampSection = () => {
  const reduce = useReducedMotion();

  return (
    <Section id="bootcamp" index={3} accent="orange" className="border-t border-line">
      <div className={`${PADX} py-24 md:py-32`}>
        <div className="grid grid-cols-12 items-center gap-y-14 lg:gap-x-10">
          <div className="col-span-12 lg:col-span-5">
            <SectionLabel no="04">Bootcamp</SectionLabel>
            <MaskedLines
              as="h2"
              per="char"
              lines={['SALT2', 'BOOTCAMP']}
              className="display-type mt-8 text-[13vw] text-ink sm:text-7xl lg:text-[5.4vw] 2xl:text-[88px]"
              stagger={0.12}
            />
            <MaskedLines
              lines={[['経験は、問わない。'], ['短期集中で、AIネイティブ人材へ。']]}
              className="mt-7 text-[15px] font-medium leading-relaxed tracking-display-ja text-gray-700 md:text-[17px]"
              delay={0.25}
            />
            <motion.p
              className="mt-5 max-w-[36em] text-[13px] leading-relaxed text-gray-600"
              initial={reduce ? false : { opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-10%' }}
              transition={{ duration: 0.7, ease: EASE_EXPO, delay: 0.35 }}
            >
              開発経験がなくても大丈夫。どなたでも応募できる実案件ベースの短期集中プログラムで、AI駆動開発・AIコンサルティングの実践力を身につけられます。
            </motion.p>

            {/* 開催日をこのページに複製せず、公式LPの最新案内を正本とする。 */}
            <motion.a
              href={BOOTCAMP_NOTICE.href}
              target="_blank"
              rel="noopener noreferrer"
              data-cursor="APPLY"
              className="group mt-8 flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5 bg-ink px-5 py-4"
              initial={reduce ? false : { opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-10%' }}
              transition={{ duration: 0.7, ease: EASE_EXPO, delay: 0.4 }}
            >
              <span className="flex items-center gap-3">
                <span className="h-1.5 w-1.5 shrink-0 bg-live" />
                <span className="text-[13px] font-bold tracking-display-ja text-paper">
                  {BOOTCAMP_NOTICE.title}
                </span>
              </span>
              <span className="flex items-center gap-2 text-[11px] font-semibold tracking-label text-paper/70">
                {BOOTCAMP_NOTICE.detail}
                <ArrowUpRight
                  size={14}
                  className="text-paper transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                  aria-hidden="true"
                />
              </span>
            </motion.a>

            <div className="mt-10 border-t border-line">
              {BOOTCAMP_PROGRAMS.map((p, i) => (
                <motion.a
                  key={p.name}
                  href={BOOTCAMP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-cursor="APPLY"
                  className="group flex items-baseline justify-between gap-4 border-b border-line py-5"
                  initial={reduce ? false : { opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-10%' }}
                  transition={{ duration: 0.7, ease: EASE_EXPO, delay: 0.15 + i * 0.08 }}
                >
                  <span className="flex items-baseline gap-4">
                    <span className="text-[11px] font-semibold tracking-label text-gray-500 tnum">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span className="text-base font-bold tracking-display-ja text-ink transition-transform duration-500 ease-expo group-hover:translate-x-1.5 md:text-lg">
                      {p.name}
                    </span>
                  </span>
                  <span className="flex shrink-0 items-center gap-3">
                    <span className="hidden text-xs text-gray-500 tnum sm:inline">{p.meta}</span>
                    <ArrowUpRight
                      size={16}
                      className="text-ink opacity-50 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-live group-hover:opacity-100"
                      aria-hidden="true"
                    />
                  </span>
                </motion.a>
              ))}
            </div>

            <motion.div
              className="mt-10"
              initial={reduce ? false : { opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-10%' }}
              transition={{ duration: 0.7, ease: EASE_EXPO, delay: 0.3 }}
            >
              <MagneticCTA href={BOOTCAMP_URL} variant="live" icon="up" cursorLabel="APPLY">
                応募する
              </MagneticCTA>
            </motion.div>
          </div>

          {/* 右: オレンジの階段 — 上へ登る建築 */}
          <div className="col-span-12 lg:col-span-7">
            <LivingImage
              src="/img/architecture/orange/p03-orange-stair-edge.jpg"
              alt="オレンジのエッジが入ったコンクリートの大階段"
              mode="reveal"
              radius={280}
              curtain
              sweep
              className="aspect-[16/10] w-full xl:mr-[-56px] xl:w-[calc(100%+56px)]"
            />
          </div>
        </div>
      </div>
    </Section>
  );
};

export default BootcampSection;
